/**
 * Trading Test Page
 * Advanced trading test page with price range selection, interactive chart,
 * and multiple order submission modes (random, curve-based, chart-based).
 *
 * Uses useOnboard as the single source of truth for mnemonic, wallet address,
 * and auth tokens. FiClient is initialized via useFiClient with the mnemonic
 * from useOnboard. Nonce is refreshed before every order submission batch.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { InfoClient, RawOrder, toSatoshi, fromSatoshi } from "../sdk";
import { useMarkets } from "../hooks/useMarkets";
import { useOnboard } from "../hooks/useOnboard";
import { useFiClient } from "../hooks/useFiClient";
import { useTradePrice } from "../hooks/useTradePrice";
import { useBuckets } from "../hooks/useBuckets";
import {
  useSubscription,
  SubscriptionProvider,
} from "../context/SubscriptionContext";
import { DEFAULT_BASE_URL, DEFAULT_WS_URL, DEFAULT_MNEMONIC, ORDER_PENDING_STATUSES } from "../constants";
import { PriceAmountChart } from "./trade_test_page/PriceAmountChart";
import { PriceRangeModal } from "./trade_test_page/PriceRangeModal";
import { TestResultsModal } from "./trade_test_page/TestResultsModal";
import {
  TestSubmissionButton,
  ChartOrderSubmissionButton,
  ResetChartButton,
  LoadPendingOrdersButton,
  CancelAllOrdersButton,
} from "./trade_test_page/TradingTestPageButtons";
import "./trade_test_page/tradetest.css";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OrderPoint {
  price: number;
  amount: number;
}

interface ChartOrder {
  price: number;
  amount: number;
  id: string;
  side?: "buy" | "sell" | "long" | "short";
  orderId?: string;
  isPending?: boolean;
}

/** Check whether a raw order status is considered "pending/open". */
function isPendingStatus(status: string): boolean {
  return ORDER_PENDING_STATUSES.includes(status.toUpperCase());
}

/** Map RawOrder side string to ChartOrder side. */
function mapOrderSide(
  rawSide: string,
): "long" | "short" {
  return rawSide.toUpperCase().includes("BUY") ? "long" : "short";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function TradingTestPageContent() {
  // ── Configuration state ──────────────────────────────────────────────
  const [baseURL, setBaseURL] = useState(DEFAULT_BASE_URL);
  const [marketId, setMarketId] = useState<string>("");

  // ── Single source of truth: useOnboard ───────────────────────────────
  const {
    mnemonic,
    walletAddress,
    authToken,
    loading: onboardLoading,
    error: onboardError,
    addressLoading,
    onboard: performOnboard,
    setMnemonic: setOnboardMnemonic,
    clearError: clearOnboardError,
  } = useOnboard(baseURL);

  // ── FiClient (derives wallet & signs transactions from mnemonic) ─────
  const { client: exchangeClient, isReady: exchangeReady } = useFiClient({
    baseURL,
    mnemonic: mnemonic || undefined,
  });

  // ── InfoClient (public / session-based queries) ──────────────────────
  const infoClientRef = useRef<InfoClient | null>(null);
  const baseURLRef = useRef<string>(baseURL);

  // ── Markets ──────────────────────────────────────────────────────────
  const { markets } = useMarkets({
    baseURL,
    sessionToken: "",
    autoFetchMarkets: true,
  });

  // ── Trade price (WebSocket via SubscriptionProvider) ─────────────────
  const { markPrice } = useTradePrice({
    baseURL,
    marketId: marketId || "",
    disablePolling: true,
  });

  // ── Buckets (needed for bucketId when placing orders) ────────────────
  const { buckets: hookBuckets } = useBuckets({
    baseURL,
    authToken: authToken || "",
    walletAddress: walletAddress || "",
    refreshInterval: 0,
    useWebSocket: false,
    marketId: "0",
  });

  // ── Price range configuration ────────────────────────────────────────
  const [minPrice, setMinPrice] = useState(10000);
  const [maxPrice, setMaxPrice] = useState(100000);
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  const [hasManualPriceChange, setHasManualPriceChange] = useState(false);
  const [roundingStep, setRoundingStep] = useState(100);

  // ── Order generation mode ────────────────────────────────────────────
  const [orderMode, setOrderMode] = useState<"random" | "curve">("random");
  const [curveIntensity, setCurveIntensity] = useState(2);
  const [orderSide, setOrderSide] = useState<"long" | "short">("long");

  // ── Chart orders (user-defined) & pending orders (from server) ───────
  const [chartOrders, setChartOrders] = useState<ChartOrder[]>([]);
  const [pendingOrders, setPendingOrders] = useState<ChartOrder[]>([]);

  // ── Test results ─────────────────────────────────────────────────────
  const [testResults, setTestResults] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Modal state ──────────────────────────────────────────────────────
  const [isPriceRangeModalOpen, setIsPriceRangeModalOpen] = useState(false);
  const [isTestResultsModalOpen, setIsTestResultsModalOpen] = useState(false);

  // ── Refs ──────────────────────────────────────────────────────────────
  const loadingToastIdRef = useRef<number>(0);

  // ── Sync subscription context ────────────────────────────────────────
  const {
    setAuthToken: setSubscriptionAuthToken,
    setWalletAddress: setSubscriptionWalletAddress,
  } = useSubscription();

  useEffect(() => {
    setSubscriptionAuthToken(authToken || undefined);
  }, [authToken, setSubscriptionAuthToken]);

  useEffect(() => {
    setSubscriptionWalletAddress(walletAddress || undefined);
  }, [walletAddress, setSubscriptionWalletAddress]);

  // ── Derive default market ────────────────────────────────────────────
  const defaultMarketId =
    markets.length > 0
      ? (markets.find((m) => m.active)?.index ?? markets[0]?.index ?? "")
      : null;

  useEffect(() => {
    if (defaultMarketId && !marketId) {
      setMarketId(defaultMarketId);
    }
  }, [defaultMarketId, marketId]);

  // ── Keep refs in sync ────────────────────────────────────────────────
  useEffect(() => {
    baseURLRef.current = baseURL;
  }, [baseURL]);

  useEffect(() => {
    infoClientRef.current = new InfoClient({ baseURL: baseURLRef.current });
  }, [baseURL]);

  // ── Fetch current price periodically ─────────────────────────────────
  useEffect(() => {
    if (!marketId || !infoClientRef.current) return;

    const fetchPrice = async () => {
      try {
        const ticker = await infoClientRef.current!.getTicker(marketId);
        if (ticker?.lastPrice) {
          const price =
            typeof ticker.lastPrice === "string"
              ? parseFloat(ticker.lastPrice)
              : ticker.lastPrice;
          setCurrentPrice(price);
        }
      } catch (error) {
        console.error("Failed to fetch current price:", error);
      }
    };

    fetchPrice();
    const interval = setInterval(fetchPrice, 5000);
    return () => clearInterval(interval);
  }, [marketId]);

  // ── Auto-update price range from mark price ──────────────────────────
  useEffect(() => {
    if (markPrice?.markPrice && !hasManualPriceChange) {
      const mp = markPrice.markPrice;
      if (!isNaN(mp) && mp > 1000) {
        const calcMax = Math.round((mp * 1.5) / roundingStep) * roundingStep;
        const calcMin = Math.round(mp / 1.5 / roundingStep) * roundingStep;
        if (calcMin > 0 && calcMax > calcMin) {
          setMinPrice(calcMin);
          setMaxPrice(calcMax);
        }
      }
    }
  }, [markPrice?.markPrice, hasManualPriceChange, roundingStep]);

  // ── Helpers: order generators ────────────────────────────────────────

  /** Generate random order points within the configured price range. */
  const generateRandomOrders = useCallback(
    (count: number): OrderPoint[] => {
      const orders: OrderPoint[] = [];
      for (let i = 0; i < count; i++) {
        const price = minPrice + Math.random() * (maxPrice - minPrice);
        const amount = 0.001 + Math.random() * 0.01;
        orders.push({ price, amount });
      }
      return orders.sort((a, b) => a.price - b.price);
    },
    [minPrice, maxPrice],
  );

  /** Generate curve-based order points (more orders near lower prices). */
  const generateCurveOrders = useCallback(
    (count: number): OrderPoint[] => {
      const orders: OrderPoint[] = [];
      const priceRange = maxPrice - minPrice;
      for (let i = 0; i < count; i++) {
        const normalized = Math.random();
        const curveValue = Math.pow(normalized, curveIntensity);
        const price = minPrice + curveValue * priceRange;
        const priceRatio = (maxPrice - price) / priceRange;
        const amount = 0.001 + priceRatio * 0.02;
        orders.push({ price, amount });
      }
      return orders.sort((a, b) => a.price - b.price);
    },
    [minPrice, maxPrice, curveIntensity],
  );

  // ── Helpers: resolve bucket for placement ────────────────────────────

  /** Resolve the first available bucketId. Returns null and shows toast on failure. */
  const resolveBucketId = useCallback((): string | null => {
    if (!hookBuckets || hookBuckets.length === 0) {
      toast.error("No buckets available", {
        description: "Please create a bucket first before placing orders.",
      });
      return null;
    }
    const id = hookBuckets[0].bucketId;
    if (!id) {
      toast.error("Bucket ID not found", {
        description: "First bucket has no ID. Please create or fund a bucket.",
      });
      return null;
    }
    return id;
  }, [hookBuckets]);

  /** Validate that all prerequisites for order placement are met. */
  const validateTradingPrerequisites = useCallback((): boolean => {
    if (!exchangeClient || !exchangeReady) {
      toast.error("FiClient not ready", {
        description:
          "Please enter a valid mnemonic and wait for the trading client to initialize.",
      });
      return false;
    }
    if (!marketId) {
      toast.error("No market selected", {
        description: "Please select a market from the dropdown.",
      });
      return false;
    }
    if (!walletAddress) {
      toast.error("Wallet address not available", {
        description:
          "Wallet address is being generated. Please wait or re-enter your mnemonic.",
      });
      return false;
    }
    return true;
  }, [exchangeClient, exchangeReady, marketId, walletAddress]);

  /** Refresh nonce from the server before a batch of write transactions. */
  const refreshNonce = useCallback(async (): Promise<boolean> => {
    if (!exchangeClient) return false;
    try {
      await exchangeClient.refreshNonceFromServer();
      return true;
    } catch (err: any) {
      console.error("[TradingTestPage] Nonce refresh failed:", err);
      toast.warning("Nonce refresh failed", {
        description:
          "Proceeding with cached nonce. Orders may still succeed.",
      });
      return true; // non-fatal — FiClient will fetch per-tx nonce as fallback
    }
  }, [exchangeClient]);

  // ── Core actions ─────────────────────────────────────────────────────

  /**
   * Load pending (open) orders from the server and merge onto chart.
   * Server-sourced orders (keyed by orderId) replace any optimistic entries
   * while locally-added orders without a matching server record are preserved.
   */
  const handleLoadPendingOrders = useCallback(async () => {
    if (!infoClientRef.current || !walletAddress || !marketId) {
      toast.error("Setup required", {
        description: "Please ensure wallet address and market ID are configured.",
      });
      return;
    }

    try {
      toast.info("Loading pending orders...");
      const ordersResponse =
        await infoClientRef.current.getOrders(walletAddress);
      const ordersArray = ordersResponse?.orders || [];

      const serverOrders: ChartOrder[] = ordersArray
        .filter((order: RawOrder) => {
          return (
            String(order.marketIndex) === String(marketId) &&
            isPendingStatus(order.status || "")
          );
        })
        .map((order: RawOrder) => ({
          price: fromSatoshi(order.price),
          amount: fromSatoshi(order.quantity),
          id: `pending-${order.orderId}`,
          side: mapOrderSide(order.side || ""),
          orderId: order.orderId,
          isPending: true,
        }));

      if (serverOrders.length > 0) {
        // Server returned orders — use them as the source of truth, but keep
        // any optimistic entries whose orderId wasn't found on the server yet.
        const serverOrderIds = new Set(serverOrders.map((o) => o.orderId));
        setPendingOrders((prev) => {
          const optimistic = prev.filter(
            (o) => o.orderId && !serverOrderIds.has(o.orderId),
          );
          return [...serverOrders, ...optimistic];
        });
      }
      // If server returned nothing, keep existing optimistic entries as-is.

      toast.success("Pending orders loaded", {
        description: `${serverOrders.length} orders from server.`,
      });
    } catch (error: any) {
      toast.error("Failed to load pending orders", {
        description: error.message || "Unknown error occurred",
      });
    }
  }, [walletAddress, marketId]);

  /** Submit a batch of orders (shared logic for test & chart submissions). */
  const submitOrderBatch = useCallback(
    async (
      orders: Array<{ price: number; amount: number; side: "buy" | "sell" }>,
      bucketId: string,
    ): Promise<any[]> => {
      const results: any[] = [];

      for (const order of orders) {
        try {
          const leverageStr = toSatoshi("1");
          const orderResult = await exchangeClient!.placeOrder({
            marketId,
            bucketId,
            side: order.side,
            orderType: "LIMIT",
            price: order.price,
            quantity: order.amount,
            timeInForce: "GTC",
            leverage: leverageStr,
          });

          results.push({
            success: orderResult?.success ?? false,
            order: {
              price: order.price,
              amount: order.amount,
              orderId: orderResult?.orderId,
              side: order.side,
            },
            txhash: orderResult?.txhash,
            errorMessage: orderResult?.errorMessage,
            timestamp: Date.now(),
          });
        } catch (error: any) {
          results.push({
            success: false,
            order: { price: order.price, amount: order.amount, side: order.side },
            error: error.message,
            timestamp: Date.now(),
          });
        }

        // Small delay between sequential orders to avoid nonce collisions
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      return results;
    },
    [exchangeClient, marketId],
  );

  /** Create ChartOrder entries from successful submission results. */
  const buildPendingFromResults = useCallback(
    (
      results: any[],
      side: "long" | "short",
    ): ChartOrder[] =>
      results
        .filter((r) => r.success)
        .map((r, i) => ({
          price: r.order.price,
          amount: r.order.amount,
          id: r.order.orderId
            ? `pending-${r.order.orderId}`
            : `optimistic-${Date.now()}-${i}`,
          side,
          orderId: r.order.orderId || undefined,
          isPending: true,
        })),
    [],
  );

  /** Handle "Submit Test Orders (Random/Curve)" button click. */
  const handleTestSubmission = useCallback(async () => {
    if (!validateTradingPrerequisites()) return;
    const bucketId = resolveBucketId();
    if (!bucketId) return;

    setIsSubmitting(true);
    try {
      await refreshNonce();

      const orderCount = 10;
      const orderPoints =
        orderMode === "random"
          ? generateRandomOrders(orderCount)
          : generateCurveOrders(orderCount);

      const side: "buy" | "sell" = orderSide === "long" ? "buy" : "sell";
      const orders = orderPoints.map((op) => ({ ...op, side }));

      const toastId = toast.info("Submitting test orders", {
        description: `Submitting ${orders.length} ${orderMode} ${orderSide} orders...`,
      });
      loadingToastIdRef.current = typeof toastId === "number" ? toastId : 0;

      const results = await submitOrderBatch(orders, bucketId);

      setTestResults((prev) => [...prev, ...results]);

      if (loadingToastIdRef.current !== 0) {
        toast.dismiss(loadingToastIdRef.current);
        loadingToastIdRef.current = 0;
      }

      const successCount = results.filter((r) => r.success).length;
      toast.success("Test orders submitted", {
        description: `${successCount}/${results.length} orders placed successfully.`,
      });

      // Immediately add successful orders to chart (optimistic update)
      const newPending = buildPendingFromResults(results, orderSide);
      if (newPending.length > 0) {
        setPendingOrders((prev) => [...prev, ...newPending]);
      }

      setIsTestResultsModalOpen(true);

      // Deferred server sync — gives DAG time to process submissions
      setTimeout(() => {
        handleLoadPendingOrders().catch(() => {});
      }, 3000);
    } catch (error: any) {
      if (loadingToastIdRef.current !== 0) {
        toast.dismiss(loadingToastIdRef.current);
        loadingToastIdRef.current = 0;
      }
      toast.error("Test submission failed", {
        description: error.message || "Unknown error occurred",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validateTradingPrerequisites,
    resolveBucketId,
    refreshNonce,
    orderMode,
    orderSide,
    generateRandomOrders,
    generateCurveOrders,
    submitOrderBatch,
    buildPendingFromResults,
    handleLoadPendingOrders,
  ]);

  /** Handle "Submit Chart Orders" button click. */
  const handleChartOrderSubmission = useCallback(async () => {
    if (!validateTradingPrerequisites()) return;
    if (chartOrders.length === 0) {
      toast.warning("No chart orders", {
        description: "Please add orders to the chart first.",
      });
      return;
    }
    const bucketId = resolveBucketId();
    if (!bucketId) return;

    setIsSubmitting(true);
    try {
      await refreshNonce();

      // Build side mapping for each chart order so we can reconstruct after submission
      const chartSides = chartOrders.map((co) =>
        co.side === "short" || co.side === "sell" ? "short" as const : "long" as const,
      );

      const orders = chartOrders.map((co) => ({
        price: co.price,
        amount: co.amount,
        side: (co.side === "short" || co.side === "sell" ? "sell" : "buy") as
          | "buy"
          | "sell",
      }));

      const toastId = toast.info("Submitting chart orders", {
        description: `Submitting ${chartOrders.length} chart orders...`,
      });
      loadingToastIdRef.current = typeof toastId === "number" ? toastId : 0;

      const results = await submitOrderBatch(orders, bucketId);

      setTestResults((prev) => [...prev, ...results]);

      if (loadingToastIdRef.current !== 0) {
        toast.dismiss(loadingToastIdRef.current);
        loadingToastIdRef.current = 0;
      }

      const successCount = results.filter((r) => r.success).length;
      toast.success("Chart orders submitted", {
        description: `${successCount}/${results.length} orders placed successfully.`,
      });

      // Immediately add successful chart orders as pending
      const newPending: ChartOrder[] = results
        .filter((r) => r.success)
        .map((r, i) => ({
          price: r.order.price,
          amount: r.order.amount,
          id: r.order.orderId
            ? `pending-${r.order.orderId}`
            : `optimistic-${Date.now()}-${i}`,
          side: chartSides[i] ?? ("long" as const),
          orderId: r.order.orderId || undefined,
          isPending: true,
        }));
      if (newPending.length > 0) {
        setPendingOrders((prev) => [...prev, ...newPending]);
      }

      // Clear submitted chart orders
      setChartOrders([]);
      setIsTestResultsModalOpen(true);

      // Deferred server sync
      setTimeout(() => {
        handleLoadPendingOrders().catch(() => {});
      }, 3000);
    } catch (error: any) {
      if (loadingToastIdRef.current !== 0) {
        toast.dismiss(loadingToastIdRef.current);
        loadingToastIdRef.current = 0;
      }
      toast.error("Chart order submission failed", {
        description: error.message || "Unknown error occurred",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validateTradingPrerequisites,
    resolveBucketId,
    refreshNonce,
    chartOrders,
    submitOrderBatch,
    buildPendingFromResults,
    handleLoadPendingOrders,
  ]);

  /** Reset all chart orders. */
  const handleResetChart = useCallback(() => {
    setChartOrders([]);
    toast.info("Chart reset", {
      description: "All chart orders have been cleared.",
    });
  }, []);

  /** Add a new order to the chart at the clicked position. */
  const handleAddChartOrder = useCallback(
    (price: number, amount: number) => {
      const newOrder: ChartOrder = {
        price,
        amount,
        id: `chart-${Date.now()}-${Math.random()}`,
        side: orderSide,
      };
      setChartOrders((prev) => [...prev, newOrder]);
    },
    [orderSide],
  );

  /** Remove an order from the chart (or a pending order). */
  const handleRemoveChartOrder = useCallback((id: string) => {
    setChartOrders((prev) => prev.filter((o) => o.id !== id));
    setPendingOrders((prev) => prev.filter((o) => o.id !== id));
  }, []);

  /** Cancel orders matching filter and reload pending orders. */
  const handleCancelAllOrders = useCallback(
    async (filter: "long" | "short" | "all") => {
      if (!validateTradingPrerequisites()) return;
      if (!infoClientRef.current) return;

      try {
        const ordersResponse =
          await infoClientRef.current.getOrders(walletAddress!);
        const ordersArray = ordersResponse?.orders || [];

        const ordersToCancel = ordersArray.filter((order: RawOrder) => {
          if (order.marketIndex !== marketId) return false;
          if (!isPendingStatus(order.status || "")) return false;
          if (filter === "all") return true;

          const side = (order.side || "").toUpperCase();
          if (filter === "long") return side.includes("BUY");
          if (filter === "short") return side.includes("SELL");
          return false;
        });

        if (ordersToCancel.length === 0) {
          toast.info("No orders to cancel", {
            description: `No ${filter} pending orders found.`,
          });
          return;
        }

        setIsSubmitting(true);
        toast.info("Canceling orders", {
          description: `Canceling ${ordersToCancel.length} ${filter} orders...`,
        });

        await refreshNonce();

        let successCount = 0;
        let failCount = 0;

        for (const order of ordersToCancel) {
          if (!order.orderId) continue;
          try {
            await exchangeClient!.cancelOrder({
              orderId: order.orderId,
              marketId,
            });
            successCount++;
          } catch (error: any) {
            failCount++;
            console.error("Failed to cancel order:", error);
          }
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        await handleLoadPendingOrders();

        toast.success("Orders canceled", {
          description: `${successCount} canceled successfully${failCount > 0 ? `, ${failCount} failed` : ""}.`,
        });
      } catch (error: any) {
        toast.error("Failed to cancel orders", {
          description: error.message || "Unknown error occurred",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      validateTradingPrerequisites,
      refreshNonce,
      exchangeClient,
      walletAddress,
      marketId,
      handleLoadPendingOrders,
    ],
  );

  /** Handle onboard button click — triggers auth token generation. */
  const handleOnboard = useCallback(async () => {
    if (!mnemonic) {
      toast.error("Mnemonic required", {
        description: "Please enter a mnemonic before onboarding.",
      });
      return;
    }
    await performOnboard(mnemonic, baseURL);
  }, [mnemonic, baseURL, performOnboard]);

  // ── Derived status flags for UI ──────────────────────────────────────
  const isWalletReady = !!walletAddress;
  const isClientReady = !!exchangeClient && exchangeReady;
  const isMarketSelected = !!marketId;
  const hasBuckets = hookBuckets && hookBuckets.length > 0;
  const isFullyReady =
    isWalletReady && isClientReady && isMarketSelected && hasBuckets;

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div className="App-content-row">
      <h1>Trading QA</h1>
      <p>
        Advanced trading test with price range, interactive chart, and multiple
        submission modes
      </p>

      {/* ── Configuration Section ────────────────────────────────────── */}
      <div className="App-section">
        <h2>Configuration</h2>
        <div className="trading-test-config-grid">
          <div>
            <label>
              HTTP Base URL:
              <input
                type="text"
                value={baseURL}
                onChange={(e) => setBaseURL(e.target.value)}
                className="trading-test-input"
              />
            </label>
          </div>
          <div>
            <label>
              Market:
              <select
                value={marketId || ""}
                onChange={(e) => setMarketId(e.target.value)}
                className="trading-test-select"
              >
                {markets.length === 0 ? (
                  <option value="">Loading markets...</option>
                ) : (
                  <>
                    <option value="" disabled>
                      -- Select a market --
                    </option>
                    {markets.map((market) => {
                      const id = market.index ?? market.marketIndex ?? "";
                      const name = market.moniker || market.symbol || `Market ID: ${id}`;
                      return (
                        <option key={id} value={id}>
                          {name}
                        </option>
                      );
                    })}
                  </>
                )}
              </select>
            </label>
          </div>
          <div>
            <label>
              Mnemonic:
              <input
                type="text"
                value={mnemonic}
                onChange={(e) => setOnboardMnemonic(e.target.value)}
                placeholder={DEFAULT_MNEMONIC ? "Enter mnemonic or leave for default" : "Enter mnemonic"}
                className="trading-test-input"
              />
            </label>
          </div>
          <div className="trading-test-onboard-cell">
            <button
              onClick={handleOnboard}
              disabled={!mnemonic || onboardLoading}
              className="trading-test-button trading-test-button-primary"
              style={{
                marginTop: "22px",
                opacity: !mnemonic || onboardLoading ? 0.6 : 1,
                cursor: !mnemonic || onboardLoading ? "not-allowed" : "pointer",
              }}
            >
              {onboardLoading ? "Onboarding..." : "Onboard"}
            </button>
          </div>
        </div>

        {/* ── Status indicators ────────────────────────────────────── */}
        <div className="trading-test-status-bar">
          <span className={`trading-test-status-item ${isWalletReady ? "ready" : "pending"}`}>
            {addressLoading
              ? "⏳ Generating address..."
              : isWalletReady
                ? `✓ Wallet: ${walletAddress.substring(0, 10)}...${walletAddress.substring(walletAddress.length - 6)}`
                : "○ No wallet"}
          </span>
          <span className={`trading-test-status-item ${isClientReady ? "ready" : "pending"}`}>
            {isClientReady ? "✓ FiClient ready" : "○ FiClient not initialized"}
          </span>
          <span className={`trading-test-status-item ${authToken ? "ready" : "pending"}`}>
            {authToken
              ? `✓ Auth: ${authToken.substring(0, 12)}...`
              : "○ Not authenticated"}
          </span>
          <span className={`trading-test-status-item ${hasBuckets ? "ready" : "pending"}`}>
            {hasBuckets
              ? `✓ ${hookBuckets!.length} bucket${hookBuckets!.length > 1 ? "s" : ""}`
              : "○ No buckets"}
          </span>
          {currentPrice && (
            <span className="trading-test-status-item price">
              Price: {currentPrice.toLocaleString()} USDC
            </span>
          )}
        </div>

        {onboardError && (
          <div className="trading-test-error-bar">
            <span>❌ {onboardError}</span>
            <button
              onClick={clearOnboardError}
              className="trading-test-button-dismiss"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>

      {/* ── Long / Short Tabs ────────────────────────────────────────── */}
      <div className="trading-test-tabs">
        <button
          className={`trading-test-tab ${orderSide === "long" ? "active long" : ""}`}
          onClick={() => setOrderSide("long")}
        >
          Long (Buy)
        </button>
        <button
          className={`trading-test-tab ${orderSide === "short" ? "active short" : ""}`}
          onClick={() => setOrderSide("short")}
        >
          Short (Sell)
        </button>
      </div>

      {/* ── Interactive Chart ────────────────────────────────────────── */}
      <div className="App-section">
        <h2>Interactive Price-Amount Chart</h2>
        <div className="trading-test-chart-controls">
          <div className="trading-test-chart-info">
            <span>Orders on chart: {chartOrders.length}</span>
            <span>Pending orders: {pendingOrders.length}</span>
            <span
              onClick={() => setIsPriceRangeModalOpen(true)}
              style={{ cursor: "pointer", textDecoration: "underline" }}
              title="Click to configure price range"
            >
              Price range: {minPrice.toLocaleString()} –{" "}
              {maxPrice.toLocaleString()} USDC
            </span>
          </div>
        </div>
        <PriceAmountChart
          minPrice={minPrice}
          maxPrice={maxPrice}
          orders={chartOrders}
          pendingOrders={pendingOrders}
          onAddOrder={handleAddChartOrder}
          onRemoveOrder={handleRemoveChartOrder}
          currentPrice={currentPrice}
          markPrice={markPrice?.markPrice ?? null}
        />
        <div className="price-amount-chart-instructions">
          <p>
            Click on the chart to add{" "}
            <strong>{orderSide === "long" ? "long (buy)" : "short (sell)"}</strong>{" "}
            orders. Click on existing orders to remove them.
          </p>
          <p>
            Y-axis: Price (USDC) &nbsp;|&nbsp; X-axis: Amount (BTC) &nbsp;|&nbsp;
            <span style={{ color: "#28a745" }}>● Long</span>{" "}
            <span style={{ color: "#dc3545" }}>● Short</span>{" "}
            <span style={{ color: "#00ff00" }}>― Current Price</span>{" "}
            <span style={{ color: "#ff0000" }}>― Mark Price</span>
          </p>
        </div>
      </div>

      {/* ── Order Submission ─────────────────────────────────────────── */}
      <div className="App-section">
        <h2>Order Submission</h2>
        <div className="trading-test-submission-buttons">
          <ResetChartButton
            onClick={handleResetChart}
            disabled={chartOrders.length === 0}
          />
          <TestSubmissionButton
            onClick={handleTestSubmission}
            disabled={isSubmitting || !isFullyReady}
            orderMode={orderMode}
          />
          <ChartOrderSubmissionButton
            onClick={handleChartOrderSubmission}
            disabled={isSubmitting || chartOrders.length === 0 || !isFullyReady}
            orderCount={chartOrders.length}
          />
        </div>

        <h2>Pending Orders Management</h2>
        <div className="trading-test-pending-orders-controls">
          <LoadPendingOrdersButton
            onClick={handleLoadPendingOrders}
            disabled={!marketId || !walletAddress}
            pendingCount={pendingOrders.length}
          />
          <div className="trading-test-cancel-buttons">
            <CancelAllOrdersButton
              onClick={() => handleCancelAllOrders("long")}
              disabled={isSubmitting || !isFullyReady}
              filter="long"
            />
            <CancelAllOrdersButton
              onClick={() => handleCancelAllOrders("short")}
              disabled={isSubmitting || !isFullyReady}
              filter="short"
            />
            <CancelAllOrdersButton
              onClick={() => handleCancelAllOrders("all")}
              disabled={isSubmitting || !isFullyReady}
              filter="all"
            />
          </div>
        </div>
      </div>

      {/* ── Price Range Modal ────────────────────────────────────────── */}
      <PriceRangeModal
        isOpen={isPriceRangeModalOpen}
        onClose={() => setIsPriceRangeModalOpen(false)}
        minPrice={minPrice}
        maxPrice={maxPrice}
        onMinPriceChange={(price) => {
          setMinPrice(price);
          setHasManualPriceChange(true);
        }}
        onMaxPriceChange={(price) => {
          setMaxPrice(price);
          setHasManualPriceChange(true);
        }}
        markPrice={markPrice || null}
        hasManualPriceChange={hasManualPriceChange}
        onResetToAuto={() => {
          if (markPrice?.markPrice) {
            const mp = markPrice.markPrice;
            if (!isNaN(mp) && mp > 1000) {
              setHasManualPriceChange(false);
              const calcMax =
                Math.round((mp * 1.5) / roundingStep) * roundingStep;
              const calcMin =
                Math.round(mp / 1.5 / roundingStep) * roundingStep;
              if (calcMin > 0 && calcMax > calcMin) {
                setMinPrice(calcMin);
                setMaxPrice(calcMax);
              }
            }
          }
        }}
        orderMode={orderMode}
        onOrderModeChange={setOrderMode}
        curveIntensity={curveIntensity}
        onCurveIntensityChange={setCurveIntensity}
        roundingStep={roundingStep}
        onRoundingStepChange={setRoundingStep}
      />

      {/* ── Test Results Modal ───────────────────────────────────────── */}
      <TestResultsModal
        isOpen={isTestResultsModalOpen}
        onClose={() => setIsTestResultsModalOpen(false)}
        testResults={testResults}
        onClearResults={() => {
          setTestResults([]);
          setIsTestResultsModalOpen(false);
        }}
      />
    </div>
  );
}

/**
 * Wrapper component providing SubscriptionContext for WebSocket subscriptions.
 * Required because TradingTestPageContent uses useSubscription.
 */
export default function TradingTestPage() {
  return (
    <SubscriptionProvider defaultWsURL={DEFAULT_WS_URL}>
      <TradingTestPageContent />
    </SubscriptionProvider>
  );
}
